import React from 'react';
import { motion } from 'framer-motion';
import { Users, Globe, Award, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function About() {
  const values = [
    {
      icon: Target,
      title: 'Precision Engineering',
      description: 'Every system designed with meticulous attention to detail and rigorous testing protocols.',
    },
    {
      icon: Award,
      title: 'Industry Excellence',
      description: 'Certified to international standards with continuous innovation in security technology.',
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: 'Building long-term relationships through reliable service and responsive support.',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Strategic operations in London and New York serving clients across continents.',
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="outline" className="text-accent border-accent mb-4">
              About Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-semibold mb-6">Redefining Security Through Engineering Innovation</h1>
            <p className="text-base md:text-lg text-muted-foreground">
              For five years, Green Flux Security System has set the standard in advanced security solutions,
              protecting the world's most critical infrastructure with unmatched expertise and reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-semibold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Founded in 2020 by CEO Alan D.C Fry, Green Flux Security System emerged from a vision to transform
                  how institutions approach physical security. With a background in engineering and a deep understanding
                  of threat landscapes, Alan D.C Fry assembled a team of security experts committed to delivering solutions
                  that combine innovation with proven reliability.
                </p>
                <p>
                  From our first installation at a major London financial institution to becoming a trusted partner
                  for government facilities across two continents, we've maintained an unwavering focus on quality,
                  precision, and client success.
                </p>
                <p>
                  Today, Green Flux operates comprehensive logistics and installation centers in London and New York,
                  serving banks, embassies, hotels, and critical infrastructure projects with the same dedication
                  that defined our founding vision.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-lg overflow-hidden metallic-surface">
                <img
                  src="https://images.unsplash.com/photo-1710981855156-1dd4b48e668d?w=800&q=85"
                  alt="Security door installation detail"
                  className="w-full h-full object-cover opacity-70 mix-blend-luminosity"
                />
                <div className="noise-overlay rounded-lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">Our Values</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The principles that guide every project, every installation, and every client relationship.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center h-full">
                  <CardContent className="pt-6 space-y-4">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">Leadership</h2>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                  <div className="h-32 w-32 rounded-lg bg-primary/10 flex-shrink-0 flex items-center justify-center">
                    <Users className="h-16 w-16 text-primary" />
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-2xl font-semibold mb-2">Alan D.C Fry</h3>
                    <p className="text-accent font-medium mb-4">Chief Executive Officer & Founder</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      With over 15 years of experience in security engineering and facility protection, Alan D.C Fry founded
                      Green Flux with a mission to deliver uncompromised security through innovative design and rigorous
                      execution. Under his leadership, the company has become a trusted partner for some of the world's
                      most demanding security installations.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-16 bg-card border-t border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">Global Operations</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Strategic locations in two of the world's leading financial and commercial hubs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card>
                <CardContent className="p-8">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">London Headquarters</h3>
                  <p className="text-sm text-muted-foreground mb-4">14 Canary Wharf, London, UK</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Full logistics and warehousing facility</li>
                    <li>• Installation and technical services</li>
                    <li>• European operations center</li>
                    <li>• 24/7 emergency response</li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardContent className="p-8">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">New York Operations</h3>
                  <p className="text-sm text-muted-foreground mb-4">280 Hudson Street, NY, USA</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• North American distribution hub</li>
                    <li>• Technical support and maintenance</li>
                    <li>• Project coordination center</li>
                    <li>• Client consultation services</li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
